/**
 * @file Helper for surface settings
 */
import { GroupByTypes, ReactionType } from '@padlet/arvo'
import { processedUrl } from '@padlet/vivaldi-client'
import { isAppUsing } from 'bits/flip'
import { __ } from 'bits/intl'

const DEFAULT_MAX_GRADE = 100

const GROUP_BY_DATA_OLD = {
  [GroupByTypes.None]: {
    icon: 'close',
    name: __('No groups'),
    description: '',
  },
  [GroupByTypes.Section]: {
    icon: 'section',
    name: __('Sections'),
    description: __('Group posts by their assigned section'),
  },
}

const GROUP_BY_DATA = {
  [GroupByTypes.None]: {
    icon: 'close_small_thin',
    name: __('No groups'),
    description: '',
  },
  [GroupByTypes.Section]: {
    icon: 'section_thin',
    name: __('Sections'),
    description: __('Group posts by their assigned section'),
  },
  [GroupByTypes.Author]: {
    icon: 'edit_account_outline',
    name: __('Author'),
    description: __('Group posts by who wrote them'),
  },
  [GroupByTypes.Color]: {
    icon: 'wallpaper_type_gradient',
    name: __('Color'),
    description: __('Group posts by their assigned color'),
  },
}

const REACTIONS_DATA = {
  [ReactionType.None]: {
    icon: isAppUsing('surfaceSettingsV2') ? 'border' : 'placeholder',
    name: __('None'),
    description: __('No reactions'),
    previewIcon: 'border',
  },
  [ReactionType.Like]: {
    icon: isAppUsing('surfaceSettingsV2') ? 'heart_outline_thin' : 'favorite',
    name: __('Like'),
    description: __('Like posts'),
    previewIcon: 'heart_filled',
  },
  [ReactionType.Vote]: {
    icon: isAppUsing('surfaceSettingsV2') ? 'thumb_up_outline' : 'thumbs_up_down',
    name: __('Vote'),
    description: __('Upvote or downvote posts'),
    previewIcon: 'thumb_up_filled',
  },
  [ReactionType.Star]: {
    icon: isAppUsing('surfaceSettingsV2') ? 'star_outline' : 'star',
    name: __('Star'),
    description: __('Give posts 1-5 stars'),
    previewIcon: 'star_filled',
  },
  [ReactionType.Grade]: {
    icon: isAppUsing('surfaceSettingsV2') ? 'checkbox_person_outline' : 'tick',
    name: __('Grade'),
    description: __('Give numeric scores to posts'),
    previewIcon: 'checkbox_person_filled',
  },
}

function processedEmojiUrl(url: string): string {
  return processedUrl(url, { width: 64, height: 64, preset: 'icon' })
}

enum SortByTypes {
  ManualNewPostsFirst = 'manual_new_posts_first',
  ManualNewPostsLast = 'manual_new_posts_last',
  ReactionsAsc = 'reactions_asc',
  ReactionsDesc = 'reactions_desc',
}

type ManualSortBy = SortByTypes.ManualNewPostsFirst | SortByTypes.ManualNewPostsLast
const ManualSortByArray: ManualSortBy[] = [SortByTypes.ManualNewPostsFirst, SortByTypes.ManualNewPostsLast]

type ReactionSortBy = SortByTypes.ReactionsAsc | SortByTypes.ReactionsDesc
const ReactionSortByArray: ReactionSortBy[] = [SortByTypes.ReactionsAsc, SortByTypes.ReactionsDesc]

const ReactionSortByAscKeyArray = ['likes_lowest', 'grades_lowest', 'stars_lowest', 'votes_lowest'] as const

type ReactionSortByAscKey = typeof ReactionSortByAscKeyArray[number]

const ReactionSortByDescKeyArray = ['likes_highest', 'grades_highest', 'stars_highest', 'votes_highest'] as const

type ReactionSortByDescKey = typeof ReactionSortByDescKeyArray[number]

const REACTION_SORT_BY_DATA: Record<
  Exclude<ReactionType, 'none'>,
  Array<{ key: ReactionSortByAscKey | ReactionSortByDescKey; icon: string; title: string }>
> = {
  [ReactionType.Like]: [
    {
      key: 'likes_highest',
      icon: 'heart_arrow_down',
      title: __('Most to least'),
    },
    {
      key: 'likes_lowest',
      icon: 'heart_arrow_up',
      title: __('Least to most'),
    },
  ],
  [ReactionType.Grade]: [
    {
      key: 'grades_highest',
      icon: 'checkbox_arrow_down',
      title: __('Highest to lowest'),
    },
    {
      key: 'grades_lowest',
      icon: 'checkbox_arrow_up',
      title: __('Lowest to highest'),
    },
  ],
  [ReactionType.Star]: [
    {
      key: 'stars_highest',
      icon: 'star_arrow_down',
      title: __('Highest to lowest'),
    },
    {
      key: 'stars_lowest',
      icon: 'star_arrow_up',
      title: __('Lowest to highest'),
    },
  ],
  [ReactionType.Vote]: [
    {
      key: 'votes_highest',
      icon: 'thumb_arrow_down',
      title: __('Most to least'),
    },
    {
      key: 'votes_lowest',
      icon: 'thumb_arrow_up',
      title: __('Least to most'),
    },
  ],
}

const MANUAL_SORT_BY_DATA: Record<ManualSortBy, { key: ManualSortBy; title: string; icon: string }> = {
  manual_new_posts_first: {
    key: SortByTypes.ManualNewPostsFirst,
    title: __('New posts added first'),
    icon: 'create_to_front',
  },
  manual_new_posts_last: {
    key: SortByTypes.ManualNewPostsLast,
    title: __('New posts added last'),
    icon: 'create_to_back',
  },
}

const SORT_BY_DATA = { ...REACTION_SORT_BY_DATA, ...MANUAL_SORT_BY_DATA }

const ReactionSortToKeyMap: Record<
  ReactionSortBy,
  Record<Exclude<ReactionType, 'none'>, ReactionSortByAscKey | ReactionSortByDescKey>
> = {
  reactions_asc: {
    [ReactionType.Like]: 'likes_lowest',
    [ReactionType.Grade]: 'grades_lowest',
    [ReactionType.Star]: 'stars_lowest',
    [ReactionType.Vote]: 'votes_lowest',
  },
  reactions_desc: {
    [ReactionType.Like]: 'likes_highest',
    [ReactionType.Grade]: 'grades_highest',
    [ReactionType.Star]: 'stars_highest',
    [ReactionType.Vote]: 'votes_highest',
  },
}

export {
  GROUP_BY_DATA,
  GROUP_BY_DATA_OLD,
  REACTIONS_DATA,
  REACTION_SORT_BY_DATA,
  MANUAL_SORT_BY_DATA,
  SORT_BY_DATA,
  DEFAULT_MAX_GRADE,
  ReactionType,
  processedEmojiUrl,
  ManualSortBy,
  ManualSortByArray,
  ReactionSortBy,
  ReactionSortByArray,
  ReactionSortByAscKeyArray,
  ReactionSortByDescKeyArray,
  ReactionSortByAscKey,
  ReactionSortByDescKey,
  ReactionSortToKeyMap,
  SortByTypes,
}
